import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Search, Filter, Eye, Edit, Clock, CheckCircle, Truck, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { useLanguage } from '@/lib/language';

interface Order {
  id: string;
  order_number: string;
  sender_name: string;
  sender_phone: string;
  sender_email: string;
  sender_address: string;
  recipient_name: string;
  recipient_phone: string;
  recipient_address: string;
  amount: number;
  currency: string;
  delivery_type: string;
  service_fee: number;
  insurance_fee: number;
  total_amount: number;
  status: string;
  notes?: string;
  created_at: string;
}

export default function Admin() {
  const { t } = useLanguage();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const statusConfig = {
    'nieopłacone': {
      label: 'Nieopłacone',
      icon: AlertCircle,
      color: "bg-orange-500/10 text-orange-600 border-orange-500/20",
      bgColor: "bg-orange-500"
    },
    'w_realizacji': {
      label: 'W realizacji',
      icon: Truck,
      color: "bg-blue-500/10 text-blue-600 border-blue-500/20",
      bgColor: "bg-blue-500"
    },
    'dostarczone': {
      label: 'Dostarczone',
      icon: CheckCircle,
      color: "bg-green-500/10 text-green-600 border-green-500/20",
      bgColor: "bg-green-500"
    },
    'anulowane': {
      label: 'Anulowane',
      icon: AlertCircle,
      color: "bg-red-500/10 text-red-600 border-red-500/20",
      bgColor: "bg-red-500"
    }
  };

  const fetchOrders = async () => {
    setIsLoading(true);
    console.log('Fetching orders from database...');
    
    try {
      const { data, error } = await supabase
        .from('cash_orders_20260303')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Fetch result:', { data: data?.length || 0, error });

      if (error) {
        console.error('Error fetching orders:', error);
      } else {
        setOrders(data || []);
        setFilteredOrders(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = orders;

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.sender_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.recipient_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    setFilteredOrders(filtered);
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    console.log('Updating order status:', { orderId, newStatus });
    
    // Sprawdzenie czy status jest prawidłowy
    const validStatuses = ['nieopłacone', 'w_realizacji', 'dostarczone', 'anulowane'];
    if (!validStatuses.includes(newStatus)) {
      console.error('Invalid status:', newStatus);
      alert(`${t('admin.error')}: Invalid status ${newStatus}`);
      return;
    }
    
    try {
      console.log('Attempting to update order:', orderId, 'to status:', newStatus);
      
      const { data, error } = await supabase
        .from('cash_orders_20260303')
        .update({ status: newStatus })
        .eq('id', orderId)
        .select();

      console.log('Update result:', { data, error });

      if (error) {
        console.error('Supabase error updating status:', error);
        alert(`${t('admin.error')}: ${error.message}\n\nDetails: ${JSON.stringify(error, null, 2)}`);
      } else if (data && data.length > 0) {
        console.log('Status updated successfully:', data[0]);
        await fetchOrders();
        alert(`${t('admin.statusUpdated')} - ${newStatus}`);
      } else {
        console.warn('No rows updated - order may not exist');
        alert(`${t('admin.error')}: Order not found or no changes made`);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      alert(`${t('admin.error')}: ${error}\n\nPlease check console for details`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status: string) => {
    return statusConfig[status as keyof typeof statusConfig] || statusConfig['nieopłacone'];
  };

  const getStatusStats = () => {
    const stats = {
      total: orders.length,
      nieopłacone: orders.filter(o => o.status === 'nieopłacone').length,
      w_realizacji: orders.filter(o => o.status === 'w_realizacji').length,
      dostarczone: orders.filter(o => o.status === 'dostarczone').length,
      anulowane: orders.filter(o => o.status === 'anulowane').length
    };
    return stats;
  };

  const testStatusUpdate = async () => {
    console.log('=== TESTING STATUS UPDATE ===');
    
    // Znajdź pierwsze zamówienie do testu
    if (orders.length === 0) {
      alert('No orders found for testing');
      return;
    }
    
    const testOrder = orders[0];
    console.log('Testing with order:', testOrder.id, testOrder.order_number);
    
    // Test zmiany statusu na 'w_realizacji'
    try {
      const { data, error } = await supabase
        .from('cash_orders_20260303')
        .update({ status: 'w_realizacji' })
        .eq('id', testOrder.id)
        .select();
        
      console.log('Test update result:', { data, error });
      
      if (error) {
        alert(`Test FAILED: ${error.message}`);
      } else {
        alert(`Test SUCCESS: Status updated to 'confirmed'`);
        await fetchOrders();
      }
    } catch (err) {
      console.error('Test error:', err);
      alert(`Test ERROR: ${err}`);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [searchTerm, statusFilter, orders]);

  const stats = getStatusStats();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-7xl mx-auto"
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Package className="w-8 h-8 text-primary" />
                {t('admin.title')}
              </h1>
              <p className="text-muted-foreground mt-2">
                {t('admin.subtitle')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={testStatusUpdate} variant="outline" size="sm">
                Test Status
              </Button>
              <Button onClick={fetchOrders} disabled={isLoading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                {t('admin.refresh')}
              </Button>
            </div>
          </div>

          {/* Statystyki */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-primary">{stats.total}</div>
                <div className="text-sm text-muted-foreground">Razem</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">{stats.nieopłacone}</div>
                <div className="text-sm text-muted-foreground">Nieopłacone</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.w_realizacji}</div>
                <div className="text-sm text-muted-foreground">W realizacji</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{stats.dostarczone}</div>
                <div className="text-sm text-muted-foreground">Dostarczone</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{stats.anulowane}</div>
                <div className="text-sm text-muted-foreground">Anulowane</div>
              </CardContent>
            </Card>
          </div>

          {/* Filtry */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                {t('admin.filters')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    placeholder={t('admin.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder={t('admin.filterByStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Wszystkie statusy</SelectItem>
                    <SelectItem value="nieopłacone">Nieopłacone</SelectItem>
                    <SelectItem value="w_realizacji">W realizacji</SelectItem>
                    <SelectItem value="dostarczone">Dostarczone</SelectItem>
                    <SelectItem value="anulowane">Anulowane</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Lista zamówień */}
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.ordersList')} ({filteredOrders.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">{t('common.loading')}</p>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">{t('admin.noOrders')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredOrders.map((order) => {
                    const config = getStatusConfig(order.status);
                    const StatusIcon = config.icon;
                    
                    return (
                      <div key={order.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="font-mono font-bold text-lg">
                              {order.order_number}
                            </div>
                            <Badge className={config.color}>
                              <StatusIcon className="w-4 h-4 mr-1" />
                              {config.label}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(order.created_at)}
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <div className="text-sm text-muted-foreground">{t('admin.sender')}</div>
                            <div className="font-medium">{order.sender_name}</div>
                            <div className="text-sm text-muted-foreground">{order.sender_phone}</div>
                          </div>
                          <div>
                            <div className="text-sm text-muted-foreground">{t('admin.recipient')}</div>
                            <div className="font-medium">{order.recipient_name}</div>
                            <div className="text-sm text-muted-foreground">{order.recipient_phone}</div>
                          </div>
                          <div>
                            <div className="text-sm text-muted-foreground">{t('admin.amount')}</div>
                            <div className="font-bold text-lg">
                              {order.total_amount.toFixed(2)} {order.currency}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {order.delivery_type === 'express' ? t('order.express') : t('order.standard')}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => setSelectedOrder(order)}>
                                <Eye className="w-4 h-4 mr-2" />
                                {t('admin.viewDetails')}
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>{t('admin.orderDetails')} {order.order_number}</DialogTitle>
                                <DialogDescription>
                                  {t('admin.orderCreated')}: {formatDate(order.created_at)}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="grid md:grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="font-semibold mb-2">{t('admin.sender')}</h4>
                                    <div className="space-y-1 text-sm">
                                      <div><strong>{t('common.name')}:</strong> {order.sender_name}</div>
                                      <div><strong>{t('common.phone')}:</strong> {order.sender_phone}</div>
                                      <div><strong>{t('common.email')}:</strong> {order.sender_email}</div>
                                      <div><strong>{t('common.address')}:</strong> {order.sender_address}</div>
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold mb-2">{t('admin.recipient')}</h4>
                                    <div className="space-y-1 text-sm">
                                      <div><strong>{t('common.name')}:</strong> {order.recipient_name}</div>
                                      <div><strong>{t('common.phone')}:</strong> {order.recipient_phone}</div>
                                      <div><strong>{t('common.address')}:</strong> {order.recipient_address}</div>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="font-semibold mb-2">{t('admin.financialDetails')}</h4>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span>{t('order.transferAmount')}:</span>
                                      <span>{order.amount.toFixed(2)} {order.currency}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>{t('order.serviceFee')}:</span>
                                      <span>{order.service_fee.toFixed(2)} {order.currency}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>{t('order.insurance')}:</span>
                                      <span>{order.insurance_fee.toFixed(2)} {order.currency}</span>
                                    </div>
                                    <div className="flex justify-between font-semibold border-t pt-1">
                                      <span>{t('order.totalCost')}:</span>
                                      <span>{order.total_amount.toFixed(2)} {order.currency}</span>
                                    </div>
                                  </div>
                                </div>
                                {order.notes && (
                                  <div>
                                    <h4 className="font-semibold mb-2">{t('common.notes')}</h4>
                                    <p className="text-sm bg-muted p-3 rounded">{order.notes}</p>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                          
                          <div className="flex items-center gap-2">
                            <Select
                              value={order.status}
                              onValueChange={(newStatus) => updateOrderStatus(order.id, newStatus)}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nieopłacone">Nieopłacone</SelectItem>
                                <SelectItem value="w_realizacji">W realizacji</SelectItem>
                                <SelectItem value="dostarczone">Dostarczone</SelectItem>
                                <SelectItem value="anulowane">Anulowane</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}